// components/SelfPaced/CertificateVerifyPage.js
// Public certificate verification page — no auth required
// Supports light/dark theme, fully responsive
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  Award, Shield, CheckCircle2, XCircle, Hash, Calendar,
  GraduationCap, BookOpen, BarChart3, Building2, Search,
  ArrowLeft,
} from 'lucide-react';
import { AppButton, AppBadge } from '../ui';
import { verifyCertificate } from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

export default function CertificateVerifyPage() {
  const { certNo } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualInput, setManualInput] = useState(certNo || searchParams.get('id') || '');

  useEffect(() => {
    if (certNo) {
      verify(certNo);
    }
  }, [certNo]);

  const verify = async (id) => {
    if (!id?.trim()) return;
    setLoading(true);
    setError('');
    setCert(null);
    try {
      const res = await verifyCertificate(id.trim());
      const data = res.data?.data || res.data;
      if (data) {
        setCert(data);
      } else {
        setError('Certificate not found. Please check the certificate number and try again.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Certificate not found. Please check the certificate number and try again.');
      } else {
        setError('Verification failed. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      verify(manualInput.trim());
    }
  };

  return (
    <div className="spv-page">
      <div className="spv-container">
        {/* Header */}
        <div className="spv-header">
          <div className="spv-header__icon">
            <Shield size={32} />
          </div>
          <h1 className="spv-header__title">Certificate Verification</h1>
          <p className="spv-header__subtitle">
            Verify the authenticity of a course completion certificate
          </p>
        </div>

        {/* Search Form */}
        <form className="spv-search" onSubmit={handleSearch}>
          <div className="spv-search__input-wrap">
            <Hash size={16} className="spv-search__icon" />
            <input
              type="text"
              className="spv-search__input"
              placeholder="Enter certificate number (e.g. CERT-2026-00001)"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              autoFocus={!certNo}
            />
          </div>
          <AppButton type="submit" disabled={loading || !manualInput.trim()}>
            {loading ? <Spinner size="sm" animation="border" /> : <><Search size={15} /> Verify</>}
          </AppButton>
        </form>

        {/* Loading */}
        {loading && (
          <div className="spv-loading">
            <Spinner animation="border" variant="secondary" />
            <span>Verifying certificate...</span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="spv-result spv-result--invalid">
            <div className="spv-result__icon spv-result__icon--invalid">
              <XCircle size={40} />
            </div>
            <h3>Certificate Not Found</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Certificate Found */}
        {cert && !loading && (
          <div className="spv-result spv-result--valid">
            <div className="spv-result__icon spv-result__icon--valid">
              <CheckCircle2 size={40} />
            </div>
            <AppBadge color="emerald" className="spv-result__badge">
              <Shield size={12} /> Verified Certificate
            </AppBadge>

            <div className="spv-cert">
              {/* Certificate visual */}
              <div className="spv-cert__visual">
                <div className="spv-cert__award-ring">
                  <Award size={48} />
                </div>
                <span className="spv-cert__type">Certificate of Completion</span>
                <h2 className="spv-cert__course">{cert.courseName}</h2>
                <p className="spv-cert__awarded">Awarded to</p>
                <h3 className="spv-cert__student">{cert.studentName}</h3>
                {cert.instituteName && (
                  <p className="spv-cert__institute">
                    <Building2 size={14} /> {cert.instituteName}
                  </p>
                )}
              </div>

              {/* Details */}
              <div className="spv-cert__details">
                <div className="spv-cert__detail">
                  <Hash size={14} />
                  <div>
                    <span className="spv-cert__detail-label">Certificate No.</span>
                    <span className="spv-cert__detail-value">{cert.certificateNo}</span>
                  </div>
                </div>
                <div className="spv-cert__detail">
                  <Calendar size={14} />
                  <div>
                    <span className="spv-cert__detail-label">Issue Date</span>
                    <span className="spv-cert__detail-value">
                      {cert.issueDate
                        ? new Date(cert.issueDate).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                {cert.quizScore != null && (
                  <div className="spv-cert__detail">
                    <BarChart3 size={14} />
                    <div>
                      <span className="spv-cert__detail-label">Exam Score</span>
                      <span className="spv-cert__detail-value">{cert.quizScore}%</span>
                    </div>
                  </div>
                )}
                {cert.completionPct != null && (
                  <div className="spv-cert__detail">
                    <CheckCircle2 size={14} />
                    <div>
                      <span className="spv-cert__detail-label">Course Completion</span>
                      <span className="spv-cert__detail-value">{cert.completionPct}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer note */}
        <p className="spv-footer">
          This verification system confirms the authenticity of certificates issued through our platform.
        </p>
      </div>
    </div>
  );
}
